import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

type BranchSocketShape = {
  id: number;
  name?: string;
  branch_type?: string;
  latest_build?: BuildSocketShape;
};

type BranchesEventPayload = {
  project_id?: number | string;
  branches?: BranchSocketShape[];
  timestamp?: number;
};

type BuildSocketShape = {
  id?: number;
  status?: string;
  heartbeat_status?: string;
  fqdn?: string | null;
  db_name?: string | null;
  created_at?: string | null;
  created?: string | null;
  active?: boolean;
  mailpit_fqdn?: string | null;
  container_name?: string | null;
  server_id?: number | null;
  code_server_enabled?: boolean;
};

type BuildUpdatePayload = {
  project_id?: number | string;
  branch_id?: number;
  build?: BuildSocketShape;
  action?: string;
};

interface UseDeploymentSocketOptions {
  projectId?: string;
  projectNumericId?: number;
  enabled?: boolean;
  onBranchesUpdate?: (branches: BranchSocketShape[]) => void;
  onBuildUpdate?: (payload: { branchId?: number; build?: BuildSocketShape }) => void;
}

interface UseDeploymentSocketResult {
  isConnected: boolean;
  isConnecting: boolean;
}

/**
 * Lightweight websocket client for the deployments page.
 * Mirrors legacy Flask/Jinja websocket behavior:
 * - joins the project room on /socket.io namespace
 * - listens for build_status_update / branches_update (branch lists)
 * - listens for build_update (single build update)
 */
export function useDeploymentSocket({
  projectId,
  projectNumericId,
  enabled = true,
  onBranchesUpdate,
  onBuildUpdate,
}: UseDeploymentSocketOptions): UseDeploymentSocketResult {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  
  const onBranchesUpdateRef = useRef(onBranchesUpdate);
  const onBuildUpdateRef = useRef(onBuildUpdate);
  const projectNumericIdRef = useRef(projectNumericId);
  
  useEffect(() => {
    onBranchesUpdateRef.current = onBranchesUpdate;
  }, [onBranchesUpdate]);
  
  useEffect(() => {
    onBuildUpdateRef.current = onBuildUpdate;
  }, [onBuildUpdate]);
  
  useEffect(() => {
    projectNumericIdRef.current = projectNumericId;
  }, [projectNumericId]);

  useEffect(() => {
    if (!projectId || !enabled) {
      return;
    }

    let isMounted = true;
    setIsConnecting(true);

    const socketBase = window.location.origin;

    const sessionId = document.cookie.match(/session=([^;]+)/)?.[1] || '';

    const socket = io(`${socketBase}/socket.io`, {
      path: '/socket.io/',
      withCredentials: true,
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      timeout: 20000,
      autoConnect: true,
      forceNew: false,
      auth: { sessionId },
    });

    socketRef.current = socket;

    socket.on('connect_error', (error: Error) => {
      console.error('[Socket] Connection failed:', error.message);
    });

    const joinProjectRoom = () => {
      socket.emit('join_project', { project_id: projectId });
    };

    const matchesProject = (incoming?: number | string) => {
      if (incoming === undefined || incoming === null) return true;
      const incomingStr = incoming.toString();
      const expectedHash = projectId?.toString();
      const expectedNumeric = projectNumericIdRef.current !== undefined ? projectNumericIdRef.current.toString() : null;
      if (expectedHash && incomingStr === expectedHash) return true;
      if (expectedNumeric && incomingStr === expectedNumeric) return true;
      return false;
    };

    const handleBranches = (payload?: BranchesEventPayload) => {
      if (!payload) return;
      if (!matchesProject(payload.project_id)) return;
      if (Array.isArray(payload.branches)) onBranchesUpdateRef.current?.(payload.branches);
    };

    const handleBuildUpdate = (payload?: BuildUpdatePayload) => {
      if (!payload) return;
      if (!matchesProject(payload.project_id)) return;
      onBuildUpdateRef.current?.({ branchId: payload.branch_id, build: payload.build });
    };

    socket.on('connect', () => {
      if (isMounted) {
        setIsConnected(true);
        setIsConnecting(false);
      }
      joinProjectRoom();
    });

    socket.on('joined_project', () => {
    });
    
    socket.on('room_joined', () => {
    });

    socket.on('build_status_update', handleBranches);
    socket.on('branches_update', handleBranches);
    socket.on('build_update', handleBuildUpdate);

    socket.on('disconnect', () => {
      if (isMounted) {
        setIsConnected(false);
      }
    });

    return () => {
      isMounted = false;
      socket.off('connect');
      socket.off('joined_project');
      socket.off('room_joined');
      socket.off('build_status_update', handleBranches);
      socket.off('branches_update', handleBranches);
      socket.off('build_update', handleBuildUpdate);
      socket.off('disconnect');
      socket.off('connect_error');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [projectId, enabled]);

  return { isConnected, isConnecting };
}

export type { BranchSocketShape, BuildSocketShape };
